/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { CustomSnackbarComponent } from '../components/common/custom-snackbar/custom-snackbar.component';
import { CustomSnackbarData, Spinner } from '../models/notifications.model';

@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
  public spinnerSubject = new Subject<Spinner>();

  private snackbarRef: MatSnackBarRef<CustomSnackbarComponent> | null = null;
  private readonly matSnackBar: MatSnackBar = inject(MatSnackBar);
  private readonly snackbarsQueue: MatSnackBarConfig[] = [];
  private isPageVisible = true;

  constructor() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
  }

  setSpinner(showSpinner: Spinner) {
    this.spinnerSubject.next(showSpinner);
  }

  public openSnackbar({
    data,
    panelClass = 'snackbar-success',
    duration = 5000,
  }: {
    data: CustomSnackbarData;
    panelClass?: 'snackbar-success' | 'snackbar-fail' | 'snackbar-warn';
    duration?: number;
  }): void {
    if (!this.isPageVisible) {
      return;
    }

    this.snackbarsQueue.push({
      data,
      panelClass,
      duration,
      verticalPosition: 'top',
    });

    if (!this.snackbarRef) {
      this.showNext();
    }
  }

  private showNext(): void {
    if (
      this.snackbarsQueue.length === 0 ||
      this.snackbarRef ||
      !this.isPageVisible
    ) {
      return;
    }

    const config = this.snackbarsQueue.shift();
    this.snackbarRef = this.matSnackBar.openFromComponent(
      CustomSnackbarComponent,
      config,
    );
    this.snackbarRef.afterDismissed().subscribe(() => {
      this.snackbarRef = null;
      this.showNext();
    });
  }

  public openSsoSnackbar(data: CustomSnackbarData): void {
    this.matSnackBar.openFromComponent(CustomSnackbarComponent, {
      data,
      panelClass: 'snackbar-sso-error-message',
      verticalPosition: 'top',
    });
  }

  private handleVisibilityChange = (): void => {
    this.isPageVisible = document.visibilityState === 'visible';

    if (this.isPageVisible && !this.snackbarRef) {
      this.showNext();
    }
  };
}
